import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, X, Plus, Clock } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import { ComplianceLayout } from "./compliance-layout";
import { ComplaintModal } from "@/components/modals/complaint-modal";
import { differenceInHours, differenceInDays, format } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Complaint {
  id: string;
  complaintReference: string | null;
  complainantName: string | null;
  complaintCategory: string;
  complaintType: string;
  complaintStatus: string;
  receivedDate: string;
  acknowledgedDate: string | null;
  dueDate: string | null;
  resolvedDate: string | null;
  escalatedToAfca: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  open: { label: "Open", className: "bg-slate-100 text-slate-700 border-slate-300" },
  acknowledged: { label: "Acknowledged", className: "bg-blue-100 text-blue-700 border-blue-300" },
  investigating: { label: "Investigating", className: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  resolved: { label: "Resolved", className: "bg-green-100 text-green-700 border-green-300" },
  escalated: { label: "Escalated", className: "bg-red-100 text-red-700 border-red-300" },
};

const CATEGORY_LABELS: Record<string, string> = {
  service: "Service",
  product: "Product",
  fees: "Fees",
  communication: "Communication",
  advice: "Advice",
  privacy: "Privacy",
  other: "Other",
};

const STATUS_OPTIONS = [
  { value: "none", label: "All Statuses" },
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "investigating", label: "Investigating" },
  { value: "resolved", label: "Resolved" },
  { value: "escalated", label: "Escalated" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Status Badge component
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Deadline indicator
// ---------------------------------------------------------------------------

interface DeadlineIndicatorProps {
  complaint: Complaint;
}

function DeadlineIndicator({ complaint }: DeadlineIndicatorProps) {
  const now = new Date();

  // Acknowledgement: due within 1 business day (24h) of receivedDate if not acknowledged
  if (complaint.complaintStatus === "open" && !complaint.acknowledgedDate) {
    const receivedAt = new Date(complaint.receivedDate);
    const ackDueAt = new Date(receivedAt);
    ackDueAt.setDate(ackDueAt.getDate() + 1);
    const hoursLeft = differenceInHours(ackDueAt, now);

    if (hoursLeft < 0) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"
          data-testid="deadline-indicator"
        >
          <Clock className="h-3 w-3" />
          ACK OVERDUE
        </span>
      );
    }
    if (hoursLeft <= 24) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"
          data-testid="deadline-indicator"
        >
          <Clock className="h-3 w-3" />
          Ack due {hoursLeft}h
        </span>
      );
    }
  }

  // Resolution due date
  if (complaint.dueDate && !complaint.resolvedDate) {
    const dueAt = new Date(complaint.dueDate);
    const daysLeft = differenceInDays(dueAt, now);

    if (daysLeft < 0) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"
          data-testid="deadline-indicator"
        >
          <Clock className="h-3 w-3" />
          OVERDUE
        </span>
      );
    }
    if (daysLeft <= 7) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"
          data-testid="deadline-indicator"
        >
          <Clock className="h-3 w-3" />
          {daysLeft}d left
        </span>
      );
    }
    return (
      <span className="text-xs text-muted-foreground">
        {daysLeft}d left
      </span>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function ComplaintsListContent() {
  const [, setLocation] = useLocation();
  const [isComplaintModalOpen, setIsComplaintModalOpen] = useState(false);

  const [search, setSearch] = useUrlState("search", "", { debounceMs: 300 });
  const [status, setStatus] = useUrlState("status", "none");

  // Build query params
  const queryParams = new URLSearchParams();
  if (search) queryParams.set("search", search);
  if (status !== "none") queryParams.set("status", status);

  const { data, isLoading } = useQuery<Complaint[]>({
    queryKey: ["/api/compliance/complaints", search, status],
    queryFn: async () => {
      const url = `/api/compliance/complaints${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const complaints = Array.isArray(data) ? data : [];

  const hasActiveFilters = search !== "" || status !== "none";

  const clearSearch = () => setSearch("");

  const handleRowClick = (complaint: Complaint) => {
    setLocation(`/compliance/complaints/${complaint.id}`);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header row with title and action button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Complaints Register</h1>
          <p className="text-sm text-muted-foreground">
            IDR complaint management per ASIC RG 271
          </p>
        </div>
        <Button
          data-testid="button-log-complaint"
          onClick={() => setIsComplaintModalOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Log Complaint
        </Button>
      </div>

      {/* Filters row */}
      <Card className="p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name or reference..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-9"
              data-testid="input-search-complaints"
            />
            {search && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
                data-testid="button-clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status filter */}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger
              className="w-[175px]"
              data-testid="select-filter-status"
            >
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearch("");
                setStatus("none");
              }}
              className="text-muted-foreground"
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        {hasActiveFilters && (
          <div className="text-xs text-muted-foreground mt-2">
            Showing {complaints.length} complaint{complaints.length !== 1 ? "s" : ""}
          </div>
        )}
      </Card>

      {/* Complaints table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Complainant</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Resolution Due</TableHead>
              <TableHead>Deadline</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : complaints.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-12 text-center text-muted-foreground"
                >
                  <div className="space-y-2">
                    <p>No complaints found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="button-log-complaint-empty"
                      onClick={() => setIsComplaintModalOpen(true)}
                    >
                      Log Complaint
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              complaints.map((complaint) => (
                <TableRow
                  key={complaint.id}
                  data-testid={`row-complaint-${complaint.id}`}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(complaint)}
                >
                  <TableCell className="font-mono text-sm">
                    {complaint.complaintReference ?? "-"}
                  </TableCell>
                  <TableCell className="font-medium">
                    {complaint.complainantName ?? "-"}
                  </TableCell>
                  <TableCell>
                    {CATEGORY_LABELS[complaint.complaintCategory] ?? complaint.complaintCategory}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={complaint.complaintStatus} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(complaint.receivedDate)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(complaint.dueDate)}
                  </TableCell>
                  <TableCell>
                    <DeadlineIndicator complaint={complaint} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <ComplaintModal
        open={isComplaintModalOpen}
        onOpenChange={setIsComplaintModalOpen}
        onSuccess={(id) => setLocation(`/compliance/complaints/${id}`)}
      />
    </div>
  );
}

export default function ComplaintsPage() {
  return (
    <ComplianceLayout>
      <ComplaintsListContent />
    </ComplianceLayout>
  );
}
